import { c as _c } from "react/compiler-runtime";
import { HEX_ALPHA, Text } from "@flat-ui";
import { PALETTE_VARS } from "@styles/palette-vars";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { Box, Stack } from "@mui-wrapper";
/** Caption shown over the globe while no project has a location. */
const OVERLAY_TITLE = "See where your projects are";

/** Follow-up sentence for users who can set a location on every project. */
const OVERLAY_SUBTITLE_EDITOR = "Add a location to a project and it shows up here. You can do that from any project in the list.";

/** Follow-up sentence for users who may not be allowed to set a location themselves. */
const OVERLAY_SUBTITLE_VIEWER = "This map shows projects that have a location. Admins and editors can add them.";

/**
 * Follow-up sentence for the archived tab. Archived projects cannot be edited, so it
 * states the rule rather than pointing at an action nobody can take here.
 */
const OVERLAY_SUBTITLE_ARCHIVED = "Archived projects appear here when they have a location.";

/** Opacity of the scrim at its darkest point, right behind the title. */
const SCRIM_CENTER_OPACITY = 0.7;

/** Opacity of the scrim halfway to its edge, where the body text sits. */
const SCRIM_MID_OPACITY = HEX_ALPHA.alpha80;

/** Opacity of the subtitle, kept below the title so the two read as a hierarchy. */
const SUBTITLE_OPACITY = HEX_ALPHA.alphaCC;

/** @returns The scrim fading from its darkest point out to transparent at the edge. */
function scrimGradient(theme) {
  function scrim(opacity) {
    return theme.alpha(theme.vars.palette.common.black, opacity);
  }
  return `radial-gradient(55% 50% at 45% 50%, ${scrim(SCRIM_CENTER_OPACITY)} 0%, ${scrim(SCRIM_MID_OPACITY)} 50%, ${scrim(0)} 70%)`;
}

/** How long the caption takes to fade out once the user starts navigating the map. */
const FADE_OUT_DURATION = "1s";
/**
 * @returns The sentence describing how projects reach the map, for the current tab and
 * the current user's ability to set a location.
 */
function getSubtitle(projectArchivingState, canEditAllProjects) {
  if (projectArchivingState === "archived") {
    return OVERLAY_SUBTITLE_ARCHIVED;
  }
  return canEditAllProjects ? OVERLAY_SUBTITLE_EDITOR : OVERLAY_SUBTITLE_VIEWER;
}

/**
 * Caption rendered over the empty globe when no project has a location yet.
 *
 * Deliberately has no card, border or button: a bordered panel over the globe reads
 * as a dialog, and the "add location" action is per project and lives on the list
 * cards, so the text points at the list instead of duplicating an action it cannot
 * scope. Pointer events stay off so the globe remains pannable underneath.
 *
 * The text never names the absence — the empty globe already shows it — so the caption
 * stays true whichever tab it is on.
 */
export function ProjectsMapEmptyOverlay(t0) {
  const $ = _c(14);
  const {
    projectArchivingState,
    isDismissed
  } = t0;
  const {
    canEditAllCompanyProjects
  } = useHasUserValidRoleCompanyLevel();
  let t1;
  if ($[0] !== canEditAllCompanyProjects || $[1] !== projectArchivingState) {
    t1 = getSubtitle(projectArchivingState, canEditAllCompanyProjects);
    $[0] = canEditAllCompanyProjects;
    $[1] = projectArchivingState;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const subtitle = t1;
  const t2 = isDismissed ? 0 : 1;
  let t3;
  if ($[3] !== t2) {
    t3 = {
      position: "absolute",
      left: "24px",
      top: "24px",
      width: "300px",
      maxWidth: "60%",
      gap: 1,
      pointerEvents: "none",
      zIndex: 1,
      opacity: t2,
      transition: `opacity ${FADE_OUT_DURATION} ease-out`
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Box aria-hidden="true" sx={{
      position: "absolute",
      inset: "-40px -40px",
      background: scrimGradient,
      filter: "blur(2px)"
    }} />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Text variant="heading16" sx={{
      position: "relative",
      color: PALETTE_VARS.common.white
    }}>{OVERLAY_TITLE}</Text>;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      position: "relative",
      color: _temp
    };
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== subtitle) {
    t7 = <Text variant="bodyS" sx={t6}>{subtitle}</Text>;
    $[8] = subtitle;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== isDismissed || $[11] !== t3 || $[12] !== t7) {
    t8 = <Stack role="status" aria-hidden={isDismissed} sx={t3}>{t4}{t5}{t7}</Stack>;
    $[10] = isDismissed;
    $[11] = t3;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  return t8;
}
function _temp(theme) {
  return theme.alpha(theme.vars.palette.common.white, SUBTITLE_OPACITY);
}